import './styles.scss'
import React, { useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { commonMethods } from 'Utils/commonMethods'
import { Skeleton } from 'primereact/skeleton'
import DialogUIModal from 'Components/UIComponent/DialogUIModal'
import { t } from 'i18next'
import { YOUR_NEWS_GUIDELINES } from '../../Utils'
import { formatTime, formatDateTime, getRepeatLabel } from '../../Utils/formatHelpers'

const YourNewsContent = ({ reminderTitle = '', selectedReminder = null, historyEntries = [], emptyMessage = 'Currently no news is available for this tab.', showGuidelines = false, isLoading = false, dateHeaderClassName = '', showScrollTopButton = true }) => {
  const [remarkGfmReady, setRemarkGfmReady] = useState(false)
  const contentRef = useRef(null)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [copiedEntryId, setCopiedEntryId] = useState(null)
  const [isOpenInfoModal, setIsOpenInfoModal] = useState(false)

  const { dateFormatBasedOnUser, utcTimeToLocalTimeString } = commonMethods

  useEffect(() => {
    setRemarkGfmReady(true)
  }, [])

  const handleScroll = () => {
    const el = contentRef.current

    if (!el) return
    setShowScrollTop(el.scrollTop > 200)
  }

  const handleScrollToTop = () => {
    const el = contentRef.current

    if (!el) return
    el.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleCopy = async (entryId, text) => {
    if (!text) return
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const textarea = document.createElement('textarea')

        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      setCopiedEntryId(entryId)
      setTimeout(() => {
        setCopiedEntryId((current) => (current === entryId ? null : current))
      }, 2000)
    } catch {
      // ignore copy failures
    }
  }

  const getPillLabel = (isoString) => {
    if (!isoString) return ''
    try {
      const d = new Date(isoString)
      const today = new Date()
      const isToday = d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear()

      if (isToday) return 'Today'

      const yesterday = new Date(today)

      yesterday.setDate(yesterday.getDate() - 1)
      const isYesterday = d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth() && d.getFullYear() === yesterday.getFullYear()

      if (isYesterday) return 'Yesterday'

      return dateFormatBasedOnUser(isoString, 'spaceFormat')
    } catch {
      return ''
    }
  }

  if (isLoading) {
    return (
      <section className="your-news-content" data-testid="your-news-content" ref={contentRef} onScroll={handleScroll}>
        <div className="your-news-content__filter">
          <span className="your-news-content__pill">Today</span>
        </div>
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="your-news-content__card your-news-content__card--skeleton">
            <div className="your-news-content__header">
              <Skeleton width="60%" height="20px" className="custom-skelton" />
              <Skeleton width="80px" height="16px" className="custom-skelton" />
            </div>
            <div className="your-news-content__skeleton-body">
              <Skeleton width="100%" height="14px" className="custom-skelton" />
              <Skeleton width="95%" height="14px" className="custom-skelton" />
              <Skeleton width="90%" height="14px" className="custom-skelton" />
            </div>
          </div>
        ))}
      </section>
    )
  }

  const groupedByDate = historyEntries.reduce((acc, entry) => {
    const scheduledFor = entry?.scheduled_for ?? entry?.sent_at ?? entry?.created_at
    const label = getPillLabel(scheduledFor) || 'Today'

    if (!acc[label]) acc[label] = []
    acc[label].push(entry)

    return acc
  }, {})

  if (showGuidelines) {
    return (
      <section className="your-news-content" data-testid="your-news-content" ref={contentRef} onScroll={handleScroll}>
        <div className="your-news-content__guidelines" data-testid="your-news-guidelines">
          <div className="your-news-content__guidelines-title">ABOUT YOUR NEWS</div>
          {YOUR_NEWS_GUIDELINES?.map((text, index) => (
            <div key={index} className="your-news-content__guideline-card">
              <div className="your-news-content__guideline-text">{text?.title}</div>
              <div className="your-news-content__guideline-paragraphs">
                <span className="your-news-content__guideline-paragraph">{text?.paragraphs}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    )
  }

  if (historyEntries.length === 0) {
    const message = reminderTitle ? `No content yet for "${reminderTitle}".` : emptyMessage

    return (
      <section className="your-news-content" data-testid="your-news-content" ref={contentRef} onScroll={handleScroll}>
        <div className="your-news-content__empty">{message}</div>
      </section>
    )
  }

  const infoTitle = (selectedReminder?.title || reminderTitle || '').trim() || 'No Title'
  const infoDescription = (selectedReminder?.prompt || '').trim()
  const nextRunAt = selectedReminder?.next_run_at
  const rawReminderTime = selectedReminder?.reminder_time || selectedReminder?.global_reminder_time
  const localReminderTime = rawReminderTime ? utcTimeToLocalTimeString(rawReminderTime) : ''
  const infoAlertTime = formatTime(nextRunAt || localReminderTime)
  const infoRepeat = getRepeatLabel(selectedReminder)
  const infoCreatedAt = formatDateTime(selectedReminder?.created_at)

  const infoContent = () => (
    <div className="your-news-content__info-content">
      <div className="your-news-content__info-row your-news-content__info-row--stack">
        <p className="your-news-content__info-label">Title</p>
        <div className="your-news-content__info-value">{infoTitle}</div>
      </div>
      <div className="your-news-content__info-row your-news-content__info-row--stack">
        <p className="your-news-content__info-label">Description</p>
        <div className="your-news-content__info-value">{infoDescription || 'No Description'}</div>
      </div>
      <div className="your-news-content__info-row your-news-content__info-row--stack">
        <p className="your-news-content__info-label">Alert Time</p>
        <div className="your-news-content__info-value">{infoAlertTime || '-'}</div>
      </div>
      <div className="your-news-content__info-row your-news-content__info-row--stack">
        <p className="your-news-content__info-label">Repeat</p>
        <div className="your-news-content__info-value">{infoRepeat || '-'}</div>
      </div>
      <div className="your-news-content__info-row your-news-content__info-row--stack">
        <p className="your-news-content__info-label">Created At</p>
        <div className="your-news-content__info-value">{infoCreatedAt || '-'}</div>
      </div>
    </div>
  )

  return (
    <section className="your-news-content" data-testid="your-news-content" ref={contentRef} onScroll={handleScroll}>
      {Object.entries(groupedByDate).map(([dateLabel, groupEntries]) => (
        <div key={dateLabel} className="your-news-content__date-group">
          <div className="your-news-content__filter">
            <span className={`your-news-content__pill ${dateHeaderClassName}`}>{dateLabel}</span>
          </div>
          {groupEntries.map((entry, index) => {
            const title = entry?.title ?? ''
            const aiResponse = entry?.ai_response ?? ''
            const scheduledFor = entry?.scheduled_for ?? entry?.created_at ?? null

            if (!aiResponse && !title) return null

            const entryKey = entry?.id ?? index

            return (
              <div key={entryKey} className="your-news-content__card">
                <div className="your-news-content__header">
                  <div></div>
                  {/* Just for UI References */}
                  {title && (
                    <div className="your-news-content__headline">
                      {title} <i className="pi pi-info-circle" onClick={() => setIsOpenInfoModal(true)}></i>
                    </div>
                  )}
                  {scheduledFor && <div className="your-news-content__time">{formatTime(scheduledFor)}</div>}
                </div>
                {aiResponse && (
                  <div className="your-news-content__body">
                    <ReactMarkdown
                      remarkPlugins={remarkGfmReady ? [remarkGfm] : []}
                      components={{
                        table({ children }) {
                          return <table className="your-news-content__markdown-table">{children}</table>
                        },
                        th({ children }) {
                          return <th className="your-news-content__markdown-th">{children}</th>
                        },
                        td({ children }) {
                          return <td className="your-news-content__markdown-td">{children}</td>
                        },
                        a({ node, ...props }) {
                          return (
                            <a target="_blank" rel="noopener noreferrer" {...props} className="your-news-content__link">
                              {props.children || 'Link'}
                            </a>
                          )
                        },
                        h1({ children }) {
                          return <h1 className="your-news-content__markdown-h1">{children}</h1>
                        },
                        h2({ children }) {
                          return <h2 className="your-news-content__markdown-h2">{children}</h2>
                        },
                        h3({ children }) {
                          return <h3 className="your-news-content__markdown-h3">{children}</h3>
                        },
                        ul({ children }) {
                          return <ul className="your-news-content__markdown-ul">{children}</ul>
                        },
                        ol({ children }) {
                          return <ol className="your-news-content__markdown-ol">{children}</ol>
                        },
                        li({ children }) {
                          return <li className="your-news-content__markdown-li">{children}</li>
                        },
                        p({ children }) {
                          return <p className="your-news-content__markdown-p">{children}</p>
                        },
                        hr({ children }) {
                          return <hr className="your-news-content__markdown-hr" />
                        },
                        code({ node, inline, className, children, ...props }) {
                          return inline ? (
                            <code className="your-news-content__markdown-code-inline" {...props}>
                              {children}
                            </code>
                          ) : (
                            <code className="your-news-content__markdown-code" {...props}>
                              {children}
                            </code>
                          )
                        },
                        strong({ children }) {
                          return <strong className="your-news-content__markdown-strong">{children}</strong>
                        },
                        em({ children }) {
                          return <em className="your-news-content__markdown-em">{children}</em>
                        }
                      }}
                    >
                      {aiResponse}
                    </ReactMarkdown>
                  </div>
                )}

                {aiResponse && (
                  <div className="your-news-content__footer">
                    <div className="your-news-content__copy-wrapper">
                      <i className={`pi ${copiedEntryId === entryKey ? 'pi-check' : 'pi-clone'} your-news-content__copy-icon`} onClick={() => handleCopy(entryKey, aiResponse)} />
                      <div className="your-news-content__copy-tooltip">
                        <div>{copiedEntryId === entryKey ? 'Copied' : 'Copy'}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}

      {showScrollTop && showScrollTopButton && (
        <div className="your-news-content__scroll-button-container" onClick={handleScrollToTop} data-testid="your-news-scroll-top">
          <i className="pi pi-angle-double-up your-news-content__scroll-icon" />
        </div>
      )}

      <DialogUIModal title={t('YOUR_NEWS_INFO')} showModal={isOpenInfoModal} showLogo={false} bodyContent={infoContent} handleClose={() => setIsOpenInfoModal(false)} hideCloseIcon={true} customClassName="your-news-content__info-modal" />
    </section>
  )
}

export default YourNewsContent
